import { clsx } from 'clsx';
import { format, nextFriday, nextSunday } from 'date-fns';
import isEqual from 'lodash/isEqual';
import dynamic from 'next/dynamic';
import querystring from 'querystring';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MobileMapListToggle } from '@/components/common/MobileMapListToggle';
import ErrorBoundary from '@/components/ErrorBoundary';
import { EstimatedMileageBanner } from '@/components/route/search/EstimatedMileageBanner/EstimatedMileageBanner';
import FilterPillsContainer from '@/components/route/search/FilterPills/FilterPillsContainer';
import { FilterPillsWrapper } from '@/components/route/search/FilterPillsContext/FilterPillsContext';
import Head from '@/components/route/search/Head/Head';
import SearchResultsCtx from '@/components/route/search/SearchResults/SearchResults.context';
import SearchResultsContainer from '@/components/route/search/SearchResults/SearchResultsContainer';
import Heading from '@/components/switchback/Heading/Heading';
import { breakpoint } from '@/constants/breakpoint';
import { EFilterCategories, ESearchFilters, SERP_URLS } from '@/constants/searchFilters';
import { useAppLayout } from '@/context/AppLayoutContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { useStickyToHeaderBottom } from '@/hooks/useStickyToHeaderBottom';
import useWindowSize from '@/hooks/useWindowSize';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';
import {
  applySearchFilter,
  getSearchHistogramResults,
  getSearchResults,
  setSearchPending,
} from '@/redux/modules/search';
import { fetchTags } from '@/redux/modules/tags/actions';
import {
  getSearchViewedEventData,
  lastMapPan,
  lastPaginationDelta,
  lastSearchedInSearchBar,
  lastZoomDirection,
  setFilterVehiclesShow,
} from '@/redux/selectors/analytics-selectors';
import { getCurrency } from '@/redux/selectors/currency';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getSearchResultsMeta } from '@/redux/selectors/search/meta';
import { getPageViewEventData } from '@/redux/selectors/search/page';
import { getSelectedFilter } from '@/redux/selectors/search/pricing';
import {
  getSearchResults as getSearchResultsSelector,
  isCampgroundSearchResult,
} from '@/redux/selectors/search/searchResults';
// ANALYTICS
import { trackMapToggleEvent, trackSearchMapExpandedEvent } from '@/services/analytics/maps';
import { EMapStatus } from '@/services/analytics/maps/types';
import { trackSearchEvent } from '@/services/analytics/search';
import { ERentalType } from '@/services/analytics/types';
import { trackEvent } from '@/services/track-event';
import { getItemFromLocalStorage, removeItemFromLocalStorage } from '@/utility/localstorage';
import { getIsIOSDevice, getIsMobileSafari } from '@/utility/navigator';
import { getParamAsString } from '@/utility/queryParams';

const SearchMapContainer = dynamic(
  () => import('@/components/route/search/SearchMap/SearchMapContainer'),
  { ssr: false },
);

const ResultsSort = dynamic(() => import('@/components/route/search/ResultsSort/ResultsSort'), {
  ssr: false,
});

export const getFixedQueryParams = (params: querystring.ParsedUrlQuery) => {
  const { [ESearchFilters.DATE_FROM]: from, [ESearchFilters.DATE_TO]: to, ...otherParams } = params;
  if (from && to && typeof from === 'string' && typeof to === 'string') {
    const dateFrom = new Date(from);
    const dateTo = new Date(to);
    if (
      isNaN(dateFrom.getTime()) ||
      isNaN(dateTo.getTime()) ||
      dateFrom.getTime() === dateTo.getTime()
    ) {
      return otherParams;
    }
    if (dateFrom < dateTo) {
      return {
        ...otherParams,
        [ESearchFilters.DATE_FROM]: from,
        [ESearchFilters.DATE_TO]: to,
      };
    } else {
      return {
        ...otherParams,
        [ESearchFilters.DATE_FROM]: to,
        [ESearchFilters.DATE_TO]: from,
      };
    }
  }
  return otherParams;
};

interface IProps {
  isCampgrounds?: boolean;
}

const RentalSearchTemplate = ({ isCampgrounds = false }: IProps) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const currency = useSelector(getCurrency);
  const searchMeta = useSelector(getSearchResultsMeta);
  const searchData = useSelector(getSearchViewedEventData);
  const searchResult = useSelector(getSearchResultsSelector);
  const filtersFromQuery = useSelector(getQueryParams);
  const [prevBaseUrl, { update: updatePrevBaseUrl }] = useLocalStorage('prevBaseUrl');
  const [expandMapView, setExpandMapView] = useState(false);
  const selectedFilter = useSelector(getSelectedFilter);
  const { isMobile, isAboveXL } = useBreakpoint();
  const [handledMobileScroll, setHandledMobileScroll] = useState(false);
  const [stickyHeight, setStickyHeight] = useState(0);

  const { headerHeight } = useAppLayout();

  const { stickyTopPositionRef } = useStickyToHeaderBottom(
    isMobile && !isCampgrounds ? `${headerHeight}px` : '-0.125rem',
  );

  const toggleExpandMapView = useCallback(() => {
    trackSearchMapExpandedEvent({
      mapStatus: expandMapView ? EMapStatus.COLLAPSED : EMapStatus.EXPANDED,
    });
    setExpandMapView(isExpanded => !isExpanded);
  }, [expandMapView]);

  const isDesktopMapViewExpanded = expandMapView && isAboveXL;

  // The map defaults to hidden on tablet/mobile, but will automatically open on desktop
  // This is more complex than might be expected, because of SSR/client inconsistency
  const [isMapOpen, _setIsMapOpen] = useState(false);

  // Track whether the map has been toggled since init
  const setIsMapOpen = useCallback(
    (value: boolean) => {
      _setIsMapOpen(value);
    },
    [_setIsMapOpen],
  );
  // SSR doesn't know the window width, so listen to the window size hook until it's available on the client
  // and then open the map. But only if the map hasn't been toggled previously.
  const windowWidth = useWindowSize()?.width || 0;
  const isDesktop = windowWidth >= breakpoint.xl;

  const initialParams = useQueryParams();
  const previousParams = useRef<querystring.ParsedUrlQuery>();
  const isBrowserNavigation = useRef(false); // Track if navigation is from browser back/forward
  const searchTitle = initialParams.title;

  // Track previous date values to detect changes
  const prevDatesRef = useRef({
    hadDates: false,
  });

  useEffect(() => {
    const hasPriceFilter =
      initialParams[ESearchFilters.PRICE_MIN] || initialParams[ESearchFilters.PRICE_MAX];
    const currentFrom = filtersFromQuery?.[ESearchFilters.DATE_FROM];
    const currentTo = filtersFromQuery?.[ESearchFilters.DATE_TO];
    const hasDates = !!(currentFrom && currentTo);

    // Check if dates were added or removed
    const datesAdded = !prevDatesRef.current.hadDates && hasDates;
    const datesRemoved = prevDatesRef.current.hadDates && !hasDates;
    const datesChanged = datesAdded || datesRemoved;

    // Reset price filters when dates are added or removed, but only if we have price filters set
    if (datesChanged && hasPriceFilter) {
      dispatch(
        applySearchFilter({
          [ESearchFilters.PRICE_MIN]: undefined,
          [ESearchFilters.PRICE_MAX]: undefined,
        }),
      );
    }

    // Update our tracking ref
    prevDatesRef.current.hadDates = hasDates;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersFromQuery?.[ESearchFilters.DATE_FROM], filtersFromQuery?.[ESearchFilters.DATE_TO]]);

  useEffect(() => {
    const fixedParams = getFixedQueryParams(initialParams);
    if (Object.keys(fixedParams).length > 0) {
      router.replace({ query: fixedParams });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(fetchTags());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Request data only on the client so we get querystrings and user's ip address
  useEffect(() => {
    const params = getFixedQueryParams(initialParams) || initialParams;

    if (isCampgrounds) {
      const currentFrom = params?.[ESearchFilters.DATE_FROM];
      const currentTo = params?.[ESearchFilters.DATE_TO];
      const hasDates = !!(currentFrom && currentTo);

      if (!hasDates) {
        const from = nextFriday(new Date());
        const to = nextSunday(from);
        dispatch(
          setHeaderSearchFilter({
            dates: {
              from,
              to,
            },
          }),
        );
        dispatch(
          applySearchFilter({
            [ESearchFilters.DATE_FROM]: format(from, 'yyyy-MM-dd'),
            [ESearchFilters.DATE_TO]: format(to, 'yyyy-MM-dd'),
          }),
        );
        params[ESearchFilters.DATE_FROM] = format(from, 'yyyy-MM-dd');
        params[ESearchFilters.DATE_TO] = format(to, 'yyyy-MM-dd');
      }
    }

    dispatch(getSearchResults(params, true, undefined, undefined, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, isCampgrounds]);

  // Handle browser back/forward navigation - listen to popstate events
  useEffect(() => {
    const handlePopState = () => {
      isBrowserNavigation.current = true;
    };

    // Listen to browser back/forward events
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle query param changes specifically for browser navigation
  useEffect(() => {
    if (!isBrowserNavigation.current) {
      return;
    }

    if (!filtersFromQuery || Object.keys(filtersFromQuery).length === 0) {
      isBrowserNavigation.current = false;
      return;
    }

    if (isEqual(previousParams.current, filtersFromQuery)) {
      isBrowserNavigation.current = false;
      return;
    }

    previousParams.current = filtersFromQuery;

    const params = getFixedQueryParams(filtersFromQuery) || filtersFromQuery;

    dispatch(getSearchResults(params, true, undefined, undefined, true));

    // refetch histogram if needed
    if (selectedFilter === EFilterCategories.PRICE || selectedFilter === EFilterCategories.MORE) {
      dispatch(getSearchHistogramResults(params, undefined));
    }

    isBrowserNavigation.current = false;
  }, [filtersFromQuery, selectedFilter, dispatch]);

  useEffect(() => {
    if (selectedFilter === 'Price' || selectedFilter === 'More') {
      const params = getFixedQueryParams(initialParams) || initialParams;
      dispatch(getSearchHistogramResults(params, undefined));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  useEffect(() => {
    if (isDesktop || expandMapView) {
      setIsMapOpen(true);
      return;
    }

    setIsMapOpen(false);
  }, [expandMapView, isDesktop, setIsMapOpen]);

  const resultsSectionDisplay = isMapOpen ? 'hidden xl:block' : 'block';
  // Mobile: scroll back to the top (no toggle knob animation so no need to delay)
  const onMobileToggleMap = useCallback(() => {
    if (getIsMobileSafari() || getIsIOSDevice()) {
      // For Safari, use requestAnimationFrame to avoid the smooth behavior bug
      if (typeof window === 'object') {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: 'auto' });
        });
      }
    } else {
      // For other browsers, use smooth scrolling
      typeof window === 'object' && window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const isEnabled = !isMapOpen;
    // the map should be expanded when user is in map view on MD/SM
    // breakpoint and resizes to LG
    setExpandMapView(isEnabled);
    setIsMapOpen(isEnabled);
    trackMapToggleEvent({
      isEnabled,
    });
    trackEvent({
      event: 'search/map',
      action: 'toggle',
      before: isMapOpen,
      after: isEnabled,
    });
  }, [isMapOpen, setIsMapOpen]);
  const mobileViewButtonTestId = isMapOpen ? 'search-list-btn' : 'search-map-btn';

  const eventData = useSelector(getPageViewEventData);

  useEffect(() => {
    if (eventData && searchData) {
      // Check if there is change in the query params in order to determinate if user
      // actually applies the selected filters and sees the result.
      if (isEqual(previousParams.current, initialParams)) {
        return;
      }
      previousParams.current = initialParams;

      const queryParms = eventData.queryParams;
      const {
        [ESearchFilters.PAGE_LIMIT]: pageLimit,
        [ESearchFilters.PAGE_OFFSET]: pageOffset,
        [ESearchFilters.ADDRESS]: queryAddress,
        [ESearchFilters.RENTAL_CATEGORY]: rentalCategory,
      } = queryParms;
      const offset = getParamAsString(pageOffset) ?? '';
      const limit = getParamAsString(pageLimit) ?? '';
      const address = getParamAsString(queryAddress) ?? '';
      const page = Math.ceil(Number(offset) / Number(limit)) + 1 || 1;
      const city = searchMeta.city;
      const state = searchMeta.state;
      const countryName = searchMeta.countryName;
      const searcherGeoLocation = [city, state, countryName].filter(Boolean).join(', ');

      if (!address) {
        if (city || state || countryName) {
          queryParms[ESearchFilters.GEOIP_ADDRESS] = searcherGeoLocation;
        }
      }

      lastZoomDirection(null);
      lastPaginationDelta(null);
      setFilterVehiclesShow(null);
      lastSearchedInSearchBar(null);
      lastMapPan(null);

      trackEvent({
        event: 'Search Results',
        queryParams: eventData.queryParams,
      });
      trackEvent({
        ...eventData,
        event: 'Searched for a rental',
        page,
        filters: queryParms,
        rental_category: rentalCategory || 'rv',
      });

      let searchQuery = '';
      const queryParams = (window && window.location.href.split('?')?.[1]) || '';
      if (queryParams) {
        searchQuery = `?${queryParams}`;
      }

      trackSearchEvent({
        ...searchData,
        searchQuery,
        geoLocation: address || searcherGeoLocation || null,
        searcherGeoLocation: searcherGeoLocation || null,
        isSEO: prevBaseUrl ? SERP_URLS.includes(prevBaseUrl) : false,
        experiments: searchMeta.experiments,
        experimentsData: searchMeta.experimentsData,
        isBlended: searchMeta.isBlended,
        searchFilterCategory: (rentalCategory as ERentalType) || ERentalType.RV,
        mapStatus: isAboveXL
          ? isDesktopMapViewExpanded
            ? EMapStatus.EXPANDED
            : EMapStatus.COLLAPSED
          : null,
      });
      updatePrevBaseUrl('');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAboveXL, eventData, searchData]);

  // reset search pending back to true on unmount
  useEffect(() => {
    return () => {
      dispatch(setSearchPending(Date.now()));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // scroll to the previous viewed tile after first load if item exists in local storage
  useEffect(() => {
    if (handledMobileScroll || !isMobile || searchResult.isLoading) return;

    let prevViewedIDInt = 0;
    try {
      const prevViewedID = getItemFromLocalStorage('prevViewedID');
      if (prevViewedID === null) return;

      prevViewedIDInt = parseInt(prevViewedID);
    } catch {
      return;
    }

    if (prevViewedIDInt === 0) return;

    const searchResultData = isCampgroundSearchResult(searchResult)
      ? searchResult.campgrounds.data
      : searchResult.data;
    const prevViewedIndex = searchResultData.findIndex(tile => tile.id === prevViewedIDInt);
    if (prevViewedIndex !== -1) {
      const tiles = document.querySelectorAll('a[data-testid="search-result"]');
      const tileRect = tiles[prevViewedIndex]?.getBoundingClientRect();
      if (tileRect) {
        if (stickyTopPositionRef.current && typeof window === 'object') {
          const filterBarRect = stickyTopPositionRef.current.getBoundingClientRect();
          const offset = tileRect.top - filterBarRect.bottom - 16;
          window.scrollTo({ top: window.scrollY + offset, behavior: 'instant' });
        } else {
          tiles[prevViewedIndex]?.scrollIntoView({ behavior: 'instant', block: 'center' });
        }

        removeItemFromLocalStorage('prevViewedID');
        setHandledMobileScroll(true);
      }
    }
  }, [searchResult, handledMobileScroll, isMobile, stickyTopPositionRef]);

  useEffect(() => {
    setStickyHeight(headerHeight + (stickyTopPositionRef.current?.clientHeight || 0));
  }, [headerHeight, stickyTopPositionRef]);

  const mobileToggleMapButton = (
    <MobileMapListToggle
      isMapOpen={isMapOpen}
      onToggle={onMobileToggleMap}
      testId={mobileViewButtonTestId}
      zIndex="z-30"
    />
  );

  return (
    <ErrorBoundary>
      <main className="text-gray-900 text">
        <Head />
        {/* Mobile map/list view button */}
        <div id="mobile-map-list-view-toggle">{mobileToggleMapButton}</div>
        <FilterPillsWrapper isCampgrounds={isCampgrounds}>
          <div
            className="sticky z-40 w-full overflow-hidden bg-white border-b md:z-30 border-neutral-20"
            ref={stickyTopPositionRef}>
            <div className="flex items-center px-4 overflow-auto xxxl:mx-12 scrollbar-none">
              <div className="md:overflow-auto md:whitespace-nowrap">
                <FilterPillsContainer />
              </div>
            </div>
          </div>

          <SearchResultsCtx>
            <div
              className={`relative flex ${
                !isDesktopMapViewExpanded && 'xxxl:mx-16 px-4 sm:px-10 xxxl:px-0'
              } ${
                // Give the row at least a viewport of height when the map is open so the
                // sticky map has room to stay pinned (its natural container height comes
                // from the results column, which collapses when results are few/hidden).
                // This keeps the footer directly below the pinned map — no exposed gap.
                isMapOpen ? 'md:min-h-screen' : ''
              }`}>
              {!isDesktopMapViewExpanded && (
                <section
                  className={clsx(resultsSectionDisplay, 'w-full mt-4 sm:mt-10 md:mt-0 max-w-80', {
                    'xl:w-8/12 xxl:w-3/4 xl:pr-10': isMapOpen,
                    'mx-auto': !isMapOpen,
                  })}>
                  {filtersFromQuery?.[ESearchFilters.ESTIMATED_TRIP_DISTANCE] &&
                    filtersFromQuery?.[ESearchFilters.DATE_FROM] &&
                    filtersFromQuery?.[ESearchFilters.DATE_TO] && (
                      <div
                        className="z-30 pb-5 -mx-4 -mt-4 md:top-[var(--banner-header-height)] sm:-mx-10 sm:-mt-10 md:mt-0 md:pb-0 l:mt-0 xl:mt-0 xl:-ml-16 xl:-mr-10"
                        style={
                          {
                            '--banner-header-height': `${stickyHeight + 1}px`,
                          } as React.CSSProperties
                        }>
                        <EstimatedMileageBanner />
                      </div>
                    )}
                  {
                    <>
                      <div className="flex items-center justify-between pb-5 xs:hidden md:flex">
                        <div className="hidden md:block">
                          {searchTitle && (
                            <Heading
                              className="mb-2 text-2xl text-gray-800 highlight"
                              data-testid="search-title"
                              level={1}>
                              {searchTitle}
                            </Heading>
                          )}
                        </div>
                      </div>
                      {searchTitle && (
                        <Heading
                          className="mb-4 text-base text-gray-800 md:hidden highlight"
                          level={1}>
                          {searchTitle}
                        </Heading>
                      )}
                    </>
                  }
                  <div className="pb-5">
                    <ResultsSort />
                  </div>
                  <SearchResultsContainer
                    isMapOpen={isMapOpen}
                    isCampgroundsSearchPage={isCampgrounds}
                  />
                </section>
              )}
              {/*
          Remove the map from the DOM instead of hiding it, otherwise the map breaks the page.
          Apparently the map is confused when trying to render into something without dimensions.
          This also lightens the initial mobile page load: MapBox isn't loaded/rendered until the user clicks to show it.
          */}
              {isMapOpen && (
                <section
                  style={
                    {
                      '--map-header-height': `${stickyHeight}px`,
                    } as React.CSSProperties
                  }
                  className={
                    // Fit the map to the viewport (calc(100vh - header)) so the whole map
                    // is above the fold, and stick it from md up. The parent row's
                    // md:min-h-screen (added above when the map is open) gives sticky the
                    // room it needs so the map stays pinned instead of riding up on scroll.
                    isDesktopMapViewExpanded
                      ? 'h-screen md:h-[calc(100vh-4rem)] bg-gray-100 md:sticky md:top-16 flex-1'
                      : `h-screen md:h-[calc(100vh-var(--map-header-height))] bg-gray-100 -mx-4 md:sticky md:top-[var(--map-header-height)] xl:ml-0 xl:-mr-10 xxxl:-mr-16 flex-1`
                  }>
                  <div className="h-full">
                    <SearchMapContainer
                      isDesktopMapViewExpanded={isDesktopMapViewExpanded}
                      toggleExpandMapView={toggleExpandMapView}
                    />
                  </div>
                </section>
              )}
            </div>
          </SearchResultsCtx>
        </FilterPillsWrapper>
      </main>
    </ErrorBoundary>
  );
};

export default RentalSearchTemplate;
